import { useEffect, useMemo, useState } from 'react';

import Confirm from 'components/shared/Modal/Confirm/Confirm';

import useNavigationBlocker from '../../hooks/useNavigationBlocker';

import DirtyTrackerContext from './DirtyTrackerContext';
import { useConfirmPreference } from './lib/useConfirmPreference';

/**
 * @param {Object} props
 * @param {boolean} props.isDirty - Whether there are unsaved changes
 * @param {string} props.message - Confirmation message
 * @param {string} props.confirmKey - Preference key for the confirmation, if provided it will show the "Don't show this message again" checkbox and save the preference to the user's preferences
 * @param {React.ReactNode} props.children
 */

const DirtyTracker = ({
  isDirty,
  message = 'You have unsaved changes. Are you sure you want to leave?',
  confirmKey,
  children
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const shouldEnablePreference = !!confirmKey;
  const { shouldSkipConfirm, handlePreferencePersist } = useConfirmPreference({ confirmKey });

  const { showConfirm, handleConfirm, handleCancel, triggerConfirm } = useNavigationBlocker(
    isDirty && !shouldSkipConfirm,
    message
  );

  useEffect(() => {
    if (!showConfirm && dontShowAgain) {
      setDontShowAgain(false);
    }
  }, [dontShowAgain, showConfirm]);

  const handleConfirmWithPreference = () => {
    if (dontShowAgain) {
      handlePreferencePersist(true);
    }
    setDontShowAgain(false);
    handleConfirm();
  };

  const handleCancelWithReset = () => {
    setDontShowAgain(false);
    handleCancel();
  };

  const contextValue = useMemo(
    () => ({
      triggerConfirm,
      /**
       * Wrapper for triggerConfirm with a more intuitive name
       * @param {Function} action - The action to execute if user confirms or if no changes
       * @param {boolean} [forceCheck] - Optional override for isDirty state
       */
      handleProtectedAction: (action, forceCheck) => {
        const shouldCheck = forceCheck !== undefined ? forceCheck : isDirty;
        triggerConfirm(action, shouldCheck && !shouldSkipConfirm);
      }
    }),
    [triggerConfirm, isDirty, shouldSkipConfirm]
  );

  return (
    <DirtyTrackerContext.Provider value={contextValue}>
      {children}
      <Confirm
        type="unsaved-changes"
        overlayClassName="z-[110]"
        message={message}
        isOpen={showConfirm}
        handleClose={handleCancelWithReset}
        handleContinue={handleConfirmWithPreference}
        dontShowAgain={dontShowAgain}
        onDontShowAgainChange={setDontShowAgain}
        showDontShowThisMessageAgain={shouldEnablePreference}
      />
    </DirtyTrackerContext.Provider>
  );
};

export default DirtyTracker;
